import { useAuth0 } from '@auth0/auth0-react'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { Chatbox, useSession } from '@talkjs/react'
import React, { useCallback, useEffect, useRef } from 'react'
import type Talk from 'talkjs'
import { logEvent } from '../../../analytics/analytics'
import { groupConversationId } from '../../../helpers/static-constants'

interface DineSessionChatProps {
  shareToken: string
  /** Stable session key (preferred over shareToken for the conversation id when available). */
  sessionId?: string
  sessionName?: string
  /** Reuses the page's soft signup gate for the anon "sign in to chat" nudge. */
  onSignIn: () => void
}

/**
 * Session-scoped group chat for the /dine/:shareToken page.
 *
 * Anon-identity finding: the app's only wired TalkJS identity is the authenticated `<Session>`
 * provider in WebMobileLayout (gated on `isAuthenticated && eater?.eaterId && chatToken`, server
 * token-signed). There is no anon TalkJS token/identity path, so we DEGRADE GRACEFULLY: signed-in
 * members get the live chat (reusing that same `useSession()` identity + the `<Chatbox
 * syncConversation>` pattern from restaurant/ChatDrawer); pure-anon guests get a "sign in to chat"
 * nudge that funnels into the page's existing signup gate. The rest of the page is never blocked.
 */
const DineSessionChat: React.FC<DineSessionChatProps> = ({
  shareToken,
  sessionId,
  sessionName,
  onSignIn,
}) => {
  const { isAuthenticated } = useAuth0()
  const session = useSession()
  const openedFiredRef = useRef(false)

  // The conversation id must be the group id (received here as `sessionId`) so the saved-group chat
  // resolves to this SAME conversation and the backend's groupId-keyed matching works. shareToken is
  // only a last-resort fallback if sessionId is somehow absent (won't converge with the group view).
  const conversationId = groupConversationId(sessionId) || shareToken
  // Chat is available only when the authenticated TalkJS Session is mounted (signed-in members).
  const canChat = isAuthenticated && !!session

  const syncConversation = useCallback(
    (talkSession: Talk.Session) => {
      const conversation = talkSession.getOrCreateConversation(conversationId)
      conversation.setParticipant(talkSession.me)
      conversation.setAttributes({
        subject: sessionName ?? 'Group dinner',
        custom: { role: 'dine-session-chat', share_token: shareToken },
      })
      return conversation
    },
    [conversationId, sessionName, shareToken],
  )

  useEffect(() => {
    if (canChat && !openedFiredRef.current) {
      openedFiredRef.current = true
      logEvent('session_chat_opened', {
        share_token: shareToken,
        screen_name: 'dine_session',
      })
    }
  }, [canChat, shareToken])

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        mt: 3,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <ChatBubbleOutlineIcon fontSize="small" color="primary" />
        <Typography variant="h6" fontWeight={700}>
          Group chat
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sort out the details with everyone who&apos;s coming — right here.
      </Typography>

      {canChat ? (
        <Box
          sx={{
            width: '100%',
            height: { xs: 420, md: 480 },
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Chatbox
            syncConversation={syncConversation}
            showChatHeader={false}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      ) : isAuthenticated ? (
        // Signed-in member, but the authenticated TalkJS session is still initializing.
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: { xs: 200, md: 240 },
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center',
          }}
        >
          <LockOutlinedIcon color="action" sx={{ mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Sign in to chat with your group
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can browse the picks and vote as a guest — sign in to join the
            conversation and keep everything in one place.
          </Typography>
          <Button
            variant="contained"
            onClick={onSignIn}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Sign in to chat
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default DineSessionChat
